import posthog from 'posthog-js'

import { ChainId, getChainNameFromChainId } from '@src/configs/chainInfo'
import { IToken } from '@src/types/token'
import { computeUSDAmount } from '@src/utils/balance'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores'
import { useTokensStore } from '@src/zustand_stores/tokens.store'

// Twitter conversion tracking type definitions
declare global {
    interface Window {
        twq?: (command: string, eventId: string, data?: any) => void
    }
}

const logger = createContextLogger('AnalyticsService')
/**
 * Analytics service for tracking user behavior
 * Note: PostHog must be initialized in App.tsx before using these methods
 */
class AnalyticsService {
    /**
     * Track a user action with additional properties
     * @param eventName The name of the event
     * @param properties Additional properties to track
     */
    public trackEvent(eventName: string, properties: Record<string, any> = {}): void {
        try {
            // Track the event directly with PostHog
            posthog.capture(eventName, properties)
            logger.debug(`Tracked event: ${eventName}`, properties)
        } catch (error) {
            logger.error(`Error tracking event: ${eventName}`, error)
        }
    }

    /**
     * Calculate the total USD value of a wallet based on token balances
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     * @returns The total wallet value in USD
     */
    private calculateWalletValue(address: string, chainType: string): number {
        try {
            // Get tokens from the tokens store
            const tokensStore = useTokensStore.getState()
            const tokens = tokensStore.tokens

            // Filter tokens for the given wallet address and chain
            const walletTokens = tokens.filter((token: IToken) => {
                // For EVM chains, we need to check if the token belongs to the correct chain
                if (chainType === 'EVM') {
                    return token.userBalance !== undefined && token.userBalance > BigInt(0)
                }
                // For Solana, we need to check if the token belongs to the Solana chain
                else if (chainType === 'SVM') {
                    return (
                        token.chainId === ChainId.Solana &&
                        token.userBalance !== undefined &&
                        token.userBalance > BigInt(0)
                    )
                }
                // For Tron, we need to check if the token belongs to the Tron chain
                else if (chainType === 'TRX') {
                    return (
                        token.chainId === ChainId.Tron &&
                        token.userBalance !== undefined &&
                        token.userBalance > BigInt(0)
                    )
                }
                return false
            })

            // Calculate total wallet value in USD
            let totalWalletValueUSD = 0

            walletTokens.forEach((token: IToken) => {
                if (token.userBalance && token.price_current) {
                    const tokenValueUSD = computeUSDAmount(token, token.userBalance)
                    if (tokenValueUSD !== undefined) {
                        totalWalletValueUSD += tokenValueUSD
                    }
                }
            })

            logger.debug('Calculated wallet value:', {
                address,
                chainType,
                totalWalletValueUSD,
                tokenCount: walletTokens.length,
            })

            return totalWalletValueUSD
        } catch (error) {
            logger.error('Error calculating wallet value:', error)
            return 0
        }
    }

    /**
     * Track a transaction completion with its USD value
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     * @param transactionValueUSD USD value of the transaction
     * @param milesEarned Miles earned from this transaction
     */
    public trackTransactionCompletion(
        address: string,
        chainType: string,
        transactionValueUSD?: number,
        milesEarned?: number
    ): void {
        try {
            // Track transaction completion
            if (transactionValueUSD !== undefined && transactionValueUSD > 0) {
                // Update the user's contribution metrics
                this.updateUserContribution(address, transactionValueUSD)

                // Update user miles if provided
                if (milesEarned !== undefined && milesEarned > 0) {
                    this.updateUserMiles(address, milesEarned)
                }

                // Check if a referral code was used for this transaction
                // We need to import from the application store to get the current referral code
                const { boundReferralCode } = useApplicationStore.getState()
                const hasReferralCode = !!boundReferralCode?.code
                const referralCodeValue = boundReferralCode?.code || ''

                // If a referral code was used, update the referrer's generated volume
                if (hasReferralCode && referralCodeValue) {
                    this.updateReferralVolume(referralCodeValue, transactionValueUSD)
                }

                // Track transaction completion event with referral code information
                this.trackEvent('transaction_completed', {
                    wallet_address: address,
                    chain_type: chainType,
                    transaction_value_usd: transactionValueUSD,
                    miles_earned: milesEarned || 0,
                    has_referral_code: hasReferralCode,
                    referral_code: referralCodeValue,
                    timestamp: new Date().toISOString(),
                })

                logger.debug('[AnalyticsService] Tracked transaction completion:', {
                    address,
                    chainType,
                    transactionValueUSD,
                    milesEarned,
                    hasReferralCode,
                    referralCodeValue,
                })
            }
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking transaction completion:', error)
        }
    }

    /**
     * Update the user's contribution metrics in PostHog
     * This tracks the total value contributed by the user through transactions
     * @param address The wallet address
     * @param transactionValueUSD The USD value of the current transaction
     */
    private updateUserContribution(address: string, transactionValueUSD: number): void {
        try {
            // Get current contribution values from PostHog
            const currentProperties = posthog.get_property('contribution_total_usd') || 0
            const transactionCount = posthog.get_property('contribution_transaction_count') || 0

            // Calculate new values
            const newTotalContribution = Number(currentProperties) + transactionValueUSD
            const newTransactionCount = Number(transactionCount) + 1
            const averageContribution = newTransactionCount > 0 ? newTotalContribution / newTransactionCount : 0

            // Update user properties with contribution metrics
            posthog.people.set({
                contribution_total_usd: newTotalContribution,
                contribution_transaction_count: newTransactionCount,
                contribution_average_usd: averageContribution,
                last_contribution_value_usd: transactionValueUSD,
                last_contribution_date: new Date().toISOString(),
            })

            // Also track incremental values for accurate counting
            // Note: Using direct property updates since increment may not be available in all PostHog versions
            this.trackEvent('contribution_increment', {
                contribution_total_usd: transactionValueUSD,
                contribution_transaction_count: 1,
            })

            // Track a separate event for contribution updates
            this.trackEvent('user_contribution_updated', {
                wallet_address: address,
                transaction_value_usd: transactionValueUSD,
                total_contribution_usd: newTotalContribution,
                transaction_count: newTransactionCount,
                average_contribution_usd: averageContribution,
                timestamp: new Date().toISOString(),
            })

            logger.debug('Updated user contribution metrics:', {
                address,
                transactionValueUSD,
                newTotalContribution,
                newTransactionCount,
                averageContribution,
            })
        } catch (error) {
            logger.error('Error updating user contribution metrics:', error)
        }
    }

    /**
     * Track when a user connects their wallet
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     * @param walletName The specific name of the wallet (MetaMask, Phantom, TronLink, etc.)
     */
    public trackWalletConnect(address: string, chainType: string, walletName?: string): void {
        try {
            // Get the current distinct ID (anonymous user ID)
            const distinctId = posthog.get_distinct_id()

            // Create a wallet-specific ID to use as an alias
            const walletAliasId = `${chainType}_${address}`

            // Store the wallet address in localStorage to track connected wallets
            this.storeConnectedWallet(address, chainType)

            try {
                // Create an alias between the anonymous ID and the wallet address
                posthog.alias(walletAliasId, distinctId)
            } catch (error) {
                // If alias creation fails (e.g., if the alias was already used), log the error
                logger.error('Error creating alias for wallet:', error)
            }

            // Get current wallet connection count from PostHog
            const currentConnectionCount = posthog.get_property('wallet_connection_count') || 0

            // Increment the connection count
            const newConnectionCount = Number(currentConnectionCount) + 1

            // First send a basic wallet connection event without the wallet value
            // This ensures we track the connection immediately
            this.trackEvent('wallet_connected', {
                wallet_address: address,
                chain_type: chainType,
                wallet_name: walletName || this.getWalletNameFromChainType(chainType),
                wallet_alias_id: walletAliasId,
                connection_time: new Date().toISOString(),
                wallet_connection_count: newConnectionCount,
            })

            // Get existing wallet table if it exists
            const existingWallets = posthog.get_property('connected_wallets') || []
            
            // Create wallet object to add to the table
            const walletData = {
                wallet_address: address,
                chain_type: chainType,
                wallet_name: walletName || this.getWalletNameFromChainType(chainType),
                connected_at: new Date().toISOString(),
                connection_number: newConnectionCount
            }
            
            // Check if this wallet already exists in the table
            const walletIndex = existingWallets.findIndex(
                (w: any) => w.wallet_address === address && w.chain_type === chainType
            )
            
            // If wallet exists, update it; otherwise add it to the array
            const updatedWallets = [...existingWallets]
            if (walletIndex >= 0) {
                updatedWallets[walletIndex] = {
                    ...updatedWallets[walletIndex],
                    ...walletData,
                    reconnected_at: new Date().toISOString(),
                    reconnection_count: (updatedWallets[walletIndex].reconnection_count || 0) + 1
                }
            } else {
                updatedWallets.push({
                    ...walletData,
                    first_connected_at: new Date().toISOString(),
                    reconnection_count: 0
                })
            }
            
            // Update user properties with table of wallets and general wallet properties
            posthog.people.set({
                connected_wallets: updatedWallets,
                has_connected_wallet: true,
                wallet_connection_count: newConnectionCount,
                last_connection_number: newConnectionCount,
                // Keep single wallet properties for backward compatibility
                // but now they just represent the most recently connected wallet
                wallet_address: address,
                chain_type: chainType,
                wallet_name: walletName || this.getWalletNameFromChainType(chainType),
                connected_at: new Date().toISOString(),
            })

            // Trigger Twitter conversion event for wallet connection
            try {
                if (typeof window !== 'undefined' && window.twq) {
                    // Get the wallet name to send to Twitter
                    const resolvedWalletName = walletName || this.getWalletNameFromChainType(chainType)

                    // Fire the Twitter event - initially without wallet value
                    // We'll update this with wallet value once it's calculated
                    // Use only the event tracking, the tag is already initialized in the HTML
                    window.twq('event', 'tw-ppzhv-psj9x', {
                        value: null, // This will be updated with wallet value later
                        currency: 'USD',
                        email_address: address, // Using wallet address as email address
                        contents: [
                            {
                                content_type: 'wallet',
                                content_id: address,
                                content_name: resolvedWalletName,
                                content_group_id: chainType,
                                num_items: 1,
                                content_price: null, // Will be updated with token price if available
                            },
                        ],
                    })

                    logger.debug('[AnalyticsService] Triggered Twitter conversion tracking for wallet connect', {
                        address,
                        chainType,
                        walletName: resolvedWalletName,
                    })
                }
            } catch (twitterError) {
                logger.error('[AnalyticsService] Error triggering Twitter conversion event:', twitterError)
            }

            // Use setTimeout to calculate and update wallet value after a short delay
            // This gives time for token balances to be fetched and updated in the store
            setTimeout(() => {
                try {
                    // Calculate wallet value after balances have been fetched
                    const walletValueUSD = this.calculateWalletValue(address, chainType)

                    // Track a separate wallet value update event
                    this.trackEvent('wallet_value_updated', {
                        wallet_address: address,
                        chain_type: chainType,
                        wallet_name: walletName || this.getWalletNameFromChainType(chainType),
                        wallet_value_usd: walletValueUSD,
                        update_source: 'wallet_connection',
                        timestamp: new Date().toISOString(),
                        wallet_connection_count: newConnectionCount,
                    })

                    // Get the updated wallet table with the new wallet data
                    const updatedWallets = posthog.get_property('connected_wallets') || []
                    
                    // Find the current wallet in the table
                    const walletIndex = updatedWallets.findIndex(
                        (w: any) => w.wallet_address === address && w.chain_type === chainType
                    )
                    
                    // If found, update its value
                    if (walletIndex >= 0) {
                        updatedWallets[walletIndex] = {
                            ...updatedWallets[walletIndex],
                            wallet_value_usd: walletValueUSD,
                            value_last_updated: new Date().toISOString()
                        }
                        
                        // Update the wallet table
                        posthog.people.set({
                            connected_wallets: updatedWallets,
                            // Keep the single wallet value property for backward compatibility
                            // but it now just represents the most recently updated wallet
                            wallet_value_usd: walletValueUSD,
                            last_wallet_value_update: new Date().toISOString(),
                            wallet_value_updated_by: 'connection',
                        })
                    } else {
                        // Fallback if wallet not found in table (should not happen)
                        posthog.people.set({
                            wallet_value_usd: walletValueUSD,
                            last_wallet_value_update: new Date().toISOString(),
                            wallet_value_updated_by: 'connection',
                        })
                    }

                    // Update Twitter conversion event with wallet value when available
                    // Send a more complete Twitter conversion event once we have the wallet value
                    if (typeof window !== 'undefined' && window.twq) {
                        // Always send the event once wallet value is calculated, even if zero
                        // This ensures consistent tracking with accurate wallet valuation
                        // Note: Only firing event, not initializing the tag again
                        window.twq('event', 'tw-ppzhv-psj9x', {
                            value: walletValueUSD, // Pass actual wallet value in USD
                            currency: 'USD',
                            email_address: address, // Using wallet address as email address
                            conversion_id: `${address}_${new Date().getTime()}`, // Add unique conversion ID
                            contents: [
                                {
                                    content_type: 'wallet',
                                    content_id: address,
                                    content_name: walletName || this.getWalletNameFromChainType(chainType),
                                    content_group_id: chainType,
                                    num_items: 1,
                                    content_price: walletValueUSD, // Include wallet value as content price
                                },
                            ],
                        })
                        logger.debug('[AnalyticsService] Updated Twitter conversion tracking with wallet value', {
                            address,
                            walletValueUSD,
                        })
                    }
                } catch (valueError) {
                    logger.error('[AnalyticsService] Error updating wallet value after connection:', valueError)
                }
            }, 2000) // 2 second delay to allow balances to be fetched
        } catch (error) {
            logger.error('Error tracking wallet connect:', error)
        }
    }

    /**
     * Track wallet disconnection and update the wallet table
     * Should be called from wallet disconnect logic.
     * @param address The wallet address being disconnected
     * @param chainType The chain type of the wallet being disconnected
     * @param walletName The name of the wallet being disconnected
     */
    public trackWalletDisconnect(address?: string, chainType?: string, walletName?: string) {
        try {
            // Only update the wallet table if we have address and chain type
            if (address && chainType) {
                // Get the current wallet table
                const existingWallets = posthog.get_property('connected_wallets') || []
                
                // Find the wallet being disconnected
                const walletIndex = existingWallets.findIndex(
                    (w: any) => w.wallet_address === address && w.chain_type === chainType
                )
                
                // If found, update its disconnection status
                if (walletIndex >= 0) {
                    const updatedWallets = [...existingWallets]
                    updatedWallets[walletIndex] = {
                        ...updatedWallets[walletIndex],
                        disconnected_at: new Date().toISOString(),
                        is_connected: false
                    }
                    
                    // Check if any wallets are still connected
                    const hasAnyConnectedWallet = updatedWallets.some(wallet => wallet.is_connected !== false)
                    
                    // Update the wallet table and general connection status
                    posthog.people.set({
                        connected_wallets: updatedWallets,
                        has_connected_wallet: hasAnyConnectedWallet,
                        // Update these for backward compatibility
                        disconnected_at: new Date().toISOString(),
                        wallet_address: address,
                        chain_type: chainType,
                        wallet_name: walletName || '',
                    })
                    
                    logger.info('[AnalyticsService] Updated wallet disconnection in wallet table', {
                        address,
                        chainType,
                        walletName,
                        remainingConnectedWallets: hasAnyConnectedWallet
                    })
                    return
                }
            }
            
            // Fallback for backward compatibility if wallet not found in table
            posthog.people.set({
                has_connected_wallet: false,
                disconnected_at: new Date().toISOString(),
                wallet_address: address || '',
                chain_type: chainType || '',
                wallet_name: walletName || '',
            })
            
            logger.info('[AnalyticsService] Set has_connected_wallet to false', {
                address,
                chainType,
                walletName,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking wallet disconnection', error)
        }
    }

    /**
     * Helper method to get a default wallet name from chain type
     * @param chainType The type of chain (EVM, SVM, TRX, etc.)
     * @returns A default wallet name for the chain type
     */
    private getWalletNameFromChainType(chainType: string): string {
        switch (chainType) {
            case 'EVM':
                return 'MetaMask'
            case 'SVM':
                return 'Phantom'
            case 'TRX':
                return 'TronLink'
            default:
                return chainType
        }
    }

    /**
     * Track when a specific wallet connection is attempted
     * @param walletName The specific name of the wallet (MetaMask, Phantom, TronLink, etc.)
     * @param chainType The type of chain (EVM, SVM, etc.)
     */
    public trackSpecificWalletConnectionAttempt(walletName: string, chainType: string): void {
        try {
            this.trackEvent('wallet_connection_attempt', {
                wallet_name: walletName,
                chain_type: chainType,
                attempted_at: new Date().toISOString(),
            })
        } catch (error) {
            logger.error('Error tracking wallet connection attempt:', error)
        }
    }

    /**
     * Track when a wallet connection attempt fails
     * @param error The error that occurred
     * @param walletName The specific name of the wallet (MetaMask, Phantom, TronLink, etc.)
     * @param chainType The type of chain (EVM, SVM, etc.)
     */
    public trackWalletConnectionFailed(error: any, walletName: string, chainType: string): void {
        try {
            this.trackEvent('wallet_connection_failed', {
                wallet_name: walletName,
                chain_type: chainType,
                error_message: error?.message || String(error),
                error_code: error?.code,
                failed_at: new Date().toISOString(),
            })
        } catch (trackError) {
            logger.error('Error tracking wallet connection failure:', trackError)
        }
    }

    /**
     * Track feature usage
     * @param featureName The name of the feature
     * @param properties Additional properties
     */
    public trackFeatureUsage(featureName: string, properties: Record<string, any> = {}): void {
        try {
            this.trackEvent('feature_used', {
                feature_name: featureName,
                ...properties,
                used_at: new Date().toISOString(),
            })
        } catch (error) {
            logger.error('Error tracking feature usage:', error)
        }
    }

    /**
     * Store connected wallet information in localStorage
     * @param address The wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     */
    private storeConnectedWallet(address: string, chainType: string): void {
        try {
            // Get existing connected wallets or initialize empty array
            const connectedWalletsString = localStorage.getItem('mach_connected_wallets') || '[]'
            const connectedWallets = JSON.parse(connectedWalletsString)

            // Check if this wallet is already stored
            const walletExists = connectedWallets.some(
                (wallet: { address: string; chainType: string }) =>
                    wallet.address === address && wallet.chainType === chainType
            )

            // If wallet doesn't exist, add it to the array
            if (!walletExists) {
                connectedWallets.push({
                    address,
                    chainType,
                    connectedAt: new Date().toISOString(),
                })

                // Store updated array back in localStorage
                localStorage.setItem('mach_connected_wallets', JSON.stringify(connectedWallets))
            }
        } catch (error) {
            logger.error('Error storing connected wallet:', error)
        }
    }

    /**
     * Get all connected wallets from localStorage
     * @returns Array of connected wallet objects
     */
    public getConnectedWallets(): Array<{ address: string; chainType: string; connectedAt: string }> {
        try {
            const connectedWalletsString = localStorage.getItem('mach_connected_wallets') || '[]'
            return JSON.parse(connectedWalletsString)
        } catch (error) {
            logger.error('Error getting connected wallets:', error)
            return []
        }
    }

    /**
     * Track when a user successfully enters or applies a referral code
     * @param code The referral code that was entered
     * @param walletAddress The wallet address of the user
     * @param referrer Optional wallet address of the user who provided the referral code
     * @param referralReward Optional reward amount associated with this referral
     */
    public trackSuccessfulReferral(
        code: string,
        walletAddress: string,
        referrer?: string,
        referralReward?: number
    ): void {
        try {
            this.trackEvent('successful_referral', {
                referral_code: code,
                wallet_address: walletAddress,
                referrer: referrer || '',
                referral_reward: referralReward || 0,
                timestamp: new Date().toISOString(),
            })

            logger.info('[AnalyticsService] Tracked successful referral code:', {
                code,
                walletAddress,
                referrer,
                referralReward,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking successful referral:', error)
        }
    }

    /**
     * Track when a user enters an incorrect referral code
     * @param code The referral code that was entered
     * @param walletAddress The wallet address of the user
     * @param errorMessage The error message explaining why the code was incorrect
     */
    public trackIncorrectReferral(code: string, walletAddress: string, errorMessage: string): void {
        try {
            this.trackEvent('incorrect_referral', {
                referral_code: code,
                wallet_address: walletAddress,
                error_message: errorMessage,
                timestamp: new Date().toISOString(),
            })

            logger.info('[AnalyticsService] Tracked incorrect referral code:', {
                code,
                walletAddress,
                errorMessage,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking incorrect referral:', error)
        }
    }

    /**
     * Track when a user removes a referral code
     * @param code The referral code that was removed
     * @param walletAddress The wallet address of the user
     */
    public trackReferralCodeRemoved(code: string, walletAddress: string): void {
        try {
            this.trackEvent('referral_code_removed', {
                referral_code: code,
                wallet_address: walletAddress,
                timestamp: new Date().toISOString(),
            })

            logger.info('[AnalyticsService] Tracked referral code removal:', {
                code,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking referral code removal:', error)
        }
    }

    /**
     * Update the referral volume metrics for a referral code
     * This tracks the total volume generated through a specific referral code
     * @param referralCode The referral code used in the transaction
     * @param transactionValueUSD The USD value of the transaction
     */
    private updateReferralVolume(referralCode: string, transactionValueUSD: number): void {
        try {
            // We need to find the user who owns this referral code
            // This would typically involve an API call or lookup in your system
            // For now, we'll just track the metrics against the referral code itself

            // Get current referral volume from PostHog
            // We use the referral code as the distinct ID to track metrics per referral code
            const distinctId = `referral_code_${referralCode}`

            // Get current metrics or initialize with zeros
            // Note: We're using the current user's properties as PostHog's API doesn't support
            // getting properties for other distinct IDs directly in the client
            // In a production environment, you might want to use a server-side solution for this
            const currentVolume = 0 // Initialize with zero for now
            const transactionCount = 0 // Initialize with zero for now

            // Calculate new values
            const newTotalVolume = Number(currentVolume) + transactionValueUSD
            const newTransactionCount = Number(transactionCount) + 1
            const averageVolume = newTransactionCount > 0 ? newTotalVolume / newTransactionCount : 0

            // Update the metrics in PostHog
            // We use the alias feature to track metrics per referral code
            try {
                // Create an alias between the referral code and a distinct ID if it doesn't exist
                posthog.alias(distinctId, distinctId)
            } catch (aliasError) {
                // Alias might already exist, which is fine
                logger.debug('[AnalyticsService] Alias might already exist for referral code:', referralCode)
            }

            // Track these metrics as event properties instead of user properties
            // since we can't set properties for other distinct IDs directly in the client
            // In a production environment, you might want to use a server-side solution for this
            this.trackEvent('referral_volume_increment', {
                referral_code: referralCode,
                distinct_id: distinctId,
                referral_volume_total_usd: transactionValueUSD, // Just the increment
                referral_transaction_count: 1, // Just the increment
                last_referral_transaction_value_usd: transactionValueUSD,
                last_referral_transaction_date: new Date().toISOString(),
            })

            // Also track an event for this update
            this.trackEvent('referral_volume_updated', {
                referral_code: referralCode,
                transaction_value_usd: transactionValueUSD,
                total_volume_usd: newTotalVolume,
                transaction_count: newTransactionCount,
                average_volume_usd: averageVolume,
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Updated referral volume metrics:', {
                referralCode,
                transactionValueUSD,
                newTotalVolume,
                newTransactionCount,
                averageVolume,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error updating referral volume:', error)
        }
    }

    /**
     * Update the user's total miles in PostHog
     * This tracks the total miles accumulated by the user through transactions
     * @param address The wallet address
     * @param milesEarned The miles earned in the current transaction
     */
    private updateUserMiles(address: string, milesEarned: number): void {
        try {
            // Get current miles from PostHog
            const currentMiles = posthog.get_property('total_miles') || 0

            // Calculate new total miles
            const newTotalMiles = Number(currentMiles) + milesEarned

            // Update user properties with miles metrics
            posthog.people.set({
                total_miles: newTotalMiles,
                last_miles_earned: milesEarned,
                last_miles_earned_date: new Date().toISOString(),
            })

            // Also track incremental values for accurate counting
            this.trackEvent('miles_increment', {
                miles_earned: milesEarned,
            })

            // Track a separate event for miles updates
            this.trackEvent('user_miles_updated', {
                wallet_address: address,
                miles_earned: milesEarned,
                total_miles: newTotalMiles,
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Updated user miles:', {
                address,
                milesEarned,
                newTotalMiles,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error updating user miles:', error)
        }
    }

    /**
     * Track a volume request that couldn't be fulfilled due to liquidity constraints
     * Only tracks requests where the user has sufficient balance but the quote fails due to liquidity
     * @param tokenSymbol The symbol of the token being requested (e.g., 'USDC')
     * @param requestedAmountUSD The USD value of the requested amount
     * @param walletBalanceUSD The USD value of the user's wallet balance for this token
     * @param walletAddress The user's wallet address
     * @param chainType The type of chain (EVM, SVM, etc.)
     * @param chainName The name of the chain (e.g., 'Ethereum', 'Solana')
     * @param tokenAddress The address of the token being requested
     * @param errorReason The reason the request couldn't be fulfilled
     */
    public trackUnfulfilledVolumeRequest(
        tokenSymbol: string,
        requestedAmountUSD: number,
        walletBalanceUSD: number,
        walletAddress: string,
        chainType: string,
        chainName: string,
        tokenAddress: string,
        errorReason: string
    ): void {
        try {
            // Only track if the wallet has sufficient balance (realistic request)
            // We use 90% as a threshold to account for gas fees and slight price fluctuations
            if (walletBalanceUSD >= requestedAmountUSD * 0.9) {
                this.trackEvent('unfulfilled_volume_request', {
                    token_symbol: tokenSymbol,
                    token_address: tokenAddress,
                    requested_amount_usd: requestedAmountUSD,
                    wallet_balance_usd: walletBalanceUSD,
                    wallet_address: walletAddress,
                    chain_type: chainType,
                    chain_name: chainName,
                    error_reason: errorReason,
                    is_realistic_request: true,
                    timestamp: new Date().toISOString(),
                })

                // Also update cumulative metrics for unfulfilled volume
                const currentUnfulfilledVolume = posthog.get_property('unfulfilled_volume_total_usd') || 0
                const unfulfilledRequestCount = posthog.get_property('unfulfilled_request_count') || 0

                posthog.people.set({
                    unfulfilled_volume_total_usd: Number(currentUnfulfilledVolume) + requestedAmountUSD,
                    unfulfilled_request_count: Number(unfulfilledRequestCount) + 1,
                    last_unfulfilled_request_amount_usd: requestedAmountUSD,
                    last_unfulfilled_request_date: new Date().toISOString(),
                })

                logger.info('[AnalyticsService] Tracked unfulfilled volume request:', {
                    tokenSymbol,
                    requestedAmountUSD,
                    walletBalanceUSD,
                    walletAddress,
                    chainType,
                    errorReason,
                })
            } else {
                // For unrealistic requests, we'll just log them without tracking in analytics
                logger.debug('[AnalyticsService] Ignored unrealistic volume request:', {
                    tokenSymbol,
                    requestedAmountUSD,
                    walletBalanceUSD,
                    walletAddress,
                    chainType,
                    errorReason,
                })
            }
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking unfulfilled volume request:', error)
        }
    }

    /**
     * Track successful swap with Twitter conversion tracking
     * @param sourceToken Source token symbol
     * @param sourceChain Source chain ID
     * @param sourceAmount Source token amount as string
     * @param sourceAmountUSD Source amount in USD
     * @param destinationToken Destination token symbol
     * @param destinationChain Destination chain ID
     * @param destinationAmount Destination token amount as string
     * @param destinationAmountUSD Destination amount in USD
     * @param transactionId Transaction ID (hash)
     * @param walletAddress User's wallet address
     * @param additionalData Any additional data to include
     */
    public trackSwapCompletionWithTwitter(
        sourceToken: string,
        sourceChain: string | number,
        sourceAmount: string,
        sourceAmountUSD: number,
        destinationToken: string,
        destinationChain: string | number,
        destinationAmount: string,
        destinationAmountUSD: number,
        transactionId: string | undefined,
        walletAddress: string | undefined,
        additionalData: Record<string, any> = {}
    ): void {
        try {
            // First ensure the regular tracking works
            this.trackEvent('swap_completion_twitter_tracking', {
                source_token: sourceToken,
                source_chain: sourceChain,
                source_amount: sourceAmount,
                source_amount_usd: sourceAmountUSD,
                destination_token: destinationToken,
                destination_chain: destinationChain,
                destination_amount: destinationAmount,
                destination_amount_usd: destinationAmountUSD,
                transaction_id: transactionId,
                wallet_address: walletAddress,
                ...additionalData
            });
            
            // Then fire the Twitter conversion event if twq exists
            if (typeof window !== 'undefined' && window.twq && walletAddress) {
                const totalValueUSD = destinationAmountUSD || sourceAmountUSD || 0

                // Create conversion event using Twitter's expected structure
                // Note: Only firing event, not re-initializing the tag (tag is initialized in HTML)
                // Format token identifiers as TOKEN-CHAIN (e.g., USDC-ETHEREUM) for better tracking
                const sourceTokenId = `${sourceToken}-${String(sourceChain).toUpperCase()}`
                const destinationTokenId = `${destinationToken}-${String(destinationChain).toUpperCase()}`

                window.twq('event', 'tw-ppzhv-psjk0', {
                    value: totalValueUSD, // Total value of the swap
                    currency: 'USD',
                    email_address: walletAddress, // Using wallet address in email field
                    conversion_id: transactionId, // Using transaction hash as unique conversion ID
                    contents: [
                        // Source token as first content item
                        {
                            content_type: 'crypto_token',
                            content_id: sourceTokenId,
                            content_name: sourceTokenId,
                            content_price: sourceAmountUSD / Number(sourceAmount),
                            num_items: Number(sourceAmount),
                            content_group_id: String(sourceChain),
                        },
                        // Destination token as second content item
                        {
                            content_type: 'crypto_token',
                            content_id: destinationTokenId,
                            content_name: destinationTokenId,
                            content_price: destinationAmountUSD / Number(destinationAmount),
                            num_items: Number(destinationAmount),
                            content_group_id: String(destinationChain),
                        },
                    ],
                })

                logger.debug('[AnalyticsService] Triggered Twitter conversion tracking for swap completion', {
                    sourceToken,
                    sourceTokenId,
                    destinationToken,
                    destinationTokenId,
                    totalValueUSD,
                    transactionId,
                })
            }
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking swap completion with Twitter', error)
        }
    }

    /**
     * Manually capture an exception for error tracking
     * @param error The error object to capture
     * @param additionalProperties Additional properties to include with the exception
     */
    public captureException(error: Error | unknown, additionalProperties: Record<string, any> = {}): void {
        try {
            // Convert error to a standard format
            const errorMessage = error instanceof Error ? error.message : String(error)
            const errorStack = error instanceof Error ? error.stack : undefined

            // Track the exception with properties
            this.trackEvent('exception', {
                error_message: errorMessage,
                error_stack: errorStack,
                ...additionalProperties,
                timestamp: new Date().toISOString(),
            })

            logger.error('Exception tracked:', { error: errorMessage, ...additionalProperties })
        } catch (trackError) {
            logger.error('Error tracking exception:', trackError)
        }
    }

    /**
     * Manually capture an exception for error tracking
     * @param error The error object to capture
     * @param additionalProperties Additional properties to include with the exception
     */
    public captureExceptionWithPosthog(error: Error | unknown, additionalProperties: Record<string, any> = {}): void {
        try {
            // Create properties object for the exception
            const properties = {
                ...additionalProperties,
                error_message: error instanceof Error ? error.message : String(error),
                error_name: error instanceof Error ? error.name : 'Unknown',
                error_stack: error instanceof Error ? error.stack : undefined,
                timestamp: new Date().toISOString(),
            }

            // Capture the exception in PostHog
            posthog.capture('exception', properties)

            logger.debug('[AnalyticsService] Captured exception:', {
                error,
                additionalProperties,
            })
        } catch (captureError) {
            logger.error('[AnalyticsService] Error capturing exception:', captureError)
        }
    }

    // Legacy wrap-related functions have been removed and replaced by newer enhanced implementations

    // Legacy wrap submitted function has been removed and replaced with trackWrapStarted

    // Legacy wrap completed function has been removed and replaced with trackWrapSuccess

    // This method has been replaced by the enhanced trackWrapFailed method with more detailed information

    // This method has been replaced by trackUnwrapAttempt

    // This method has been replaced by trackUnwrapStarted

    // This method has been replaced by trackUnwrapSuccess

    // This method has been replaced by the enhanced trackUnwrapFailed method with more detailed information

    /**
     * Track when a user inputs a value into the quote field
     * @param amount The amount entered by the user
     * @param tokenSymbol The symbol of the token (e.g., 'BTC', 'ETH')
     * @param amountUSD The USD value of the amount if available
     * @param walletAddress The user's wallet address if available
     * @param chainId The chain ID where the token is from
     */
    public trackQuoteInputValue(
        amount: string,
        tokenSymbol: string,
        amountUSD?: number,
        walletAddress?: string,
        chainId?: number | string
    ): void {
        try {
            this.trackEvent('quote_input_value', {
                amount: amount,
                token_symbol: tokenSymbol,
                amount_usd: amountUSD || 0,
                wallet_address: walletAddress || '',
                chain_id: chainId || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked quote input value:', {
                amount,
                tokenSymbol,
                amountUSD,
                walletAddress,
                chainId,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking quote input value:', error)
        }
    }

    /**
     * Track when a user selects a token from the token list
     * @param tokenSymbol The symbol of the selected token (e.g., 'BTC', 'ETH')
     * @param tokenAddress The address of the selected token
     * @param chainId The chain ID where the token is from
     * @param walletAddress The user's wallet address if available
     * @param tokenType Whether this is a standard token or custom token
     */
    public trackTokenSelection(
        tokenSymbol: string,
        tokenAddress: string,
        chainId: number | string,
        walletAddress?: string,
        tokenType: 'standard' | 'custom' = 'standard'
    ): void {
        try {
            this.trackEvent('token_selected', {
                token_symbol: tokenSymbol,
                token_address: tokenAddress,
                chain_id: chainId,
                wallet_address: walletAddress || '',
                token_type: tokenType,
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked token selection:', {
                tokenSymbol,
                tokenAddress,
                chainId,
                walletAddress,
                tokenType,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking token selection:', error)
        }
    }

    /**
     * Track when a user opens the token selection modal
     * @param tokenType Whether the token is source or destination
     * @param currentTokenSymbol The symbol of the current token (if any)
     * @param currentChainId The chain ID of the current selection (if any)
     * @param walletAddress The user's wallet address if available
     */
    public trackTokenModalOpen(
        tokenType: 'source' | 'destination',
        currentTokenSymbol?: string,
        currentChainId?: number | string,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('token_modal_opened', {
                token_type: tokenType,
                current_token_symbol: currentTokenSymbol || '',
                current_chain_id: currentChainId || '',
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked token modal opened:', {
                tokenType,
                currentTokenSymbol,
                currentChainId,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking token modal opened:', error)
        }
    }

    /**
     * Track when a user searches for tokens but gets no results
     * @param tokenType Whether the search is for source or destination token
     * @param searchQuery The search query that returned no results
     * @param chainId The chain ID where the search was performed (or 'all' if searching all chains)
     * @param walletAddress The user's wallet address if available
     */
    public trackTokenSearchNoResults(
        tokenType: 'source' | 'destination',
        searchQuery: string,
        chainId?: number | string | 'all',
        walletAddress?: string
    ): void {
        try {
            // Convert chain ID to chain name for better readability in analytics
            let chainName = '';
            let rawChainId = '';
            
            if (chainId === 'all') {
                chainName = 'all';
            } else if (chainId) {
                rawChainId = String(chainId);
                if (typeof chainId === 'number' || !isNaN(Number(chainId))) {
                    chainName = getChainNameFromChainId(Number(chainId)) || String(chainId);
                } else {
                    chainName = String(chainId);
                }
            }
            
            this.trackEvent('token_search_no_results', {
                token_type: tokenType,
                search_query: searchQuery,
                chain_id: rawChainId,  // Keep the original chain ID for reference
                chain_name: chainName,  // Add the readable chain name
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked token search with no results:', {
                tokenType,
                searchQuery,
                chainId,
                chainName,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking token search with no results:', error)
        }
    }

    /**
     * Track when a user types in the AI quote generator input box
     * @param promptText The text the user is typing
     * @param isFirstInput Whether this is the first input in the current session
     * @param containsTokenSymbols Whether the input contains recognized token symbols
     * @param containsAmounts Whether the input contains amount values
     */
    public trackAIPromptInput(
        promptText: string,
        isFirstInput: boolean,
        containsTokenSymbols: boolean,
        containsAmounts: boolean
    ): void {
        try {
            this.trackEvent('ai_prompt_input', {
                prompt_text: promptText,
                is_first_input: isFirstInput,
                contains_token_symbols: containsTokenSymbols,
                contains_amounts: containsAmounts,
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked AI prompt input:', {
                promptText,
                isFirstInput,
                containsTokenSymbols,
                containsAmounts,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking AI prompt input:', error)
        }
    }

    /**
     * Track when a user closes the token selection modal
     * @param tokenType Whether the modal was for source or destination token
     * @param selectedTokenSymbol The symbol of the selected token (if any)
     * @param wasTokenSelected Whether a token was selected before closing
     * @param searchQuery The last search query used (if any)
     * @param duration Duration in milliseconds the modal was open
     */
    public trackTokenModalClosed(
        tokenType: 'source' | 'destination',
        selectedTokenSymbol?: string,
        wasTokenSelected?: boolean,
        searchQuery?: string,
        duration?: number
    ): void {
        try {
            this.trackEvent('token_modal_closed', {
                token_type: tokenType,
                selected_token_symbol: selectedTokenSymbol || '',
                was_token_selected: wasTokenSelected || false,
                search_query: searchQuery || '',
                duration_ms: duration || 0,
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked token modal closed:', {
                tokenType,
                selectedTokenSymbol,
                wasTokenSelected,
                searchQuery,
                duration,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking token modal closed:', error)
        }
    }

    /**
     * Track when a user selects a chain in the token selection modal
     * @param tokenType Whether the modal is for source or destination token
     * @param chainId The ID of the selected chain
     * @param chainName The name of the selected chain (for readability)
     * @param previousChainId The previously selected chain ID, if any
     * @param searchQuery The current search query, if any
     */
    public trackChainSelected(
        tokenType: 'source' | 'destination',
        chainId: number | string,
        chainName: string,
        previousChainId?: number | string,
        searchQuery?: string
    ): void {
        try {
            this.trackEvent('chain_selected', {
                token_type: tokenType,
                chain_id: String(chainId),
                chain_name: chainName,
                previous_chain_id: previousChainId ? String(previousChainId) : '',
                search_query: searchQuery || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked chain selection in token modal:', {
                tokenType,
                chainId,
                chainName,
                previousChainId,
                searchQuery,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking chain selection:', error)
        }
    }

    /**
     * Track when a Jupiter swap is initiated
     * @param sourceToken Source token symbol
     * @param destinationToken Destination token symbol
     * @param chainId The Solana chain ID
     * @param sourceAmount Source token amount as string
     * @param sourceAmountUSD Source amount in USD
     * @param walletAddress User's wallet address
     * @param jupiterRouteDetails Optional details about the selected Jupiter route
     */

    public trackJupiterSwapInitiated(
        sourceToken: string,
        destinationToken: string,
        chainId: string | number,
        sourceAmount: string,
        sourceAmountUSD: number,
        walletAddress?: string,
        jupiterRouteDetails?: {
            routePlanLength?: number;
            ammNames?: string[];
            priceImpactPct?: string;
            prioritizationFeeLamports?: number;
        }
    ): void {
        try {
            this.trackEvent('jupiter_swap_initiated', {
                source_token: sourceToken,
                destination_token: destinationToken,
                chain_id: chainId,
                source_amount: sourceAmount,
                source_amount_usd: sourceAmountUSD,
                wallet_address: walletAddress || '',
                route_plan_length: jupiterRouteDetails?.routePlanLength || 0,
                amm_names: jupiterRouteDetails?.ammNames || [],
                price_impact_pct: jupiterRouteDetails?.priceImpactPct || '0',
                prioritization_fee_lamports: jupiterRouteDetails?.prioritizationFeeLamports || 0,
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked Jupiter swap initiated:', {
                sourceToken,
                destinationToken,
                chainId,
                sourceAmount,
                sourceAmountUSD,
                walletAddress,
                jupiterRouteDetails,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking Jupiter swap initiation:', error)
        }
    }

    /**
     * Track when a Jupiter swap is completed successfully
     * @param sourceToken Source token symbol
     * @param destinationToken Destination token symbol
     * @param chainId The Solana chain ID
     * @param sourceAmount Source token amount as string
     * @param sourceAmountUSD Source amount in USD
     * @param destinationAmount Destination token amount as string
     * @param destinationAmountUSD Destination amount in USD
     * @param txHash The transaction hash
     * @param walletAddress User's wallet address
     * @param executionTime Optional execution time in milliseconds
     * @param jupiterRouteDetails Optional details about the selected Jupiter route
     */
    public trackJupiterSwapCompleted(
        sourceToken: string,
        destinationToken: string,
        chainId: string | number,
        sourceAmount: string,
        sourceAmountUSD: number,
        destinationAmount: string,
        destinationAmountUSD: number,
        txHash: string,
        walletAddress?: string,
        executionTime?: number,
        jupiterRouteDetails?: {
            routePlanLength?: number;
            ammNames?: string[];
            priceImpactPct?: string;
        }
    ): void {
        try {
            this.trackEvent('jupiter_swap_completed', {
                source_token: sourceToken,
                destination_token: destinationToken,
                chain_id: chainId,
                source_amount: sourceAmount,
                source_amount_usd: sourceAmountUSD,
                destination_amount: destinationAmount,
                destination_amount_usd: destinationAmountUSD,
                transaction_hash: txHash,
                wallet_address: walletAddress || '',
                execution_time_ms: executionTime || 0,
                route_plan_length: jupiterRouteDetails?.routePlanLength || 0,
                amm_names: jupiterRouteDetails?.ammNames || [],
                price_impact_pct: jupiterRouteDetails?.priceImpactPct || '0',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked Jupiter swap completed:', {
                sourceToken,
                destinationToken,
                chainId,
                sourceAmount,
                sourceAmountUSD,
                destinationAmount,
                destinationAmountUSD,
                txHash,
                walletAddress,
                executionTime,
                jupiterRouteDetails,
            })

            // Also track this as a transaction completion for general metrics
            if (walletAddress) {
                this.trackTransactionCompletion(walletAddress, 'SVM', destinationAmountUSD)
            }
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking Jupiter swap completion:', error)
        }
    }

    /**
     * Track when a Jupiter swap fails
     * @param sourceToken Source token symbol
     * @param destinationToken Destination token symbol
     * @param chainId The Solana chain ID
     * @param sourceAmount Source token amount as string
     * @param sourceAmountUSD Source amount in USD
     * @param errorMessage The error message
     * @param errorCode Optional error code
     * @param txHash Optional transaction hash if available
     * @param walletAddress User's wallet address
     * @param jupiterRouteDetails Optional details about the selected Jupiter route
     */
    public trackJupiterSwapFailed(
        sourceToken: string,
        destinationToken: string,
        chainId: string | number,
        sourceAmount: string,
        sourceAmountUSD: number,
        errorMessage: string,
        errorCode?: string | number,
        txHash?: string,
        walletAddress?: string,
        jupiterRouteDetails?: {
            routePlanLength?: number;
            ammNames?: string[];
            priceImpactPct?: string;
        }
    ): void {
        try {
            this.trackEvent('jupiter_swap_failed', {
                source_token: sourceToken,
                destination_token: destinationToken,
                chain_id: chainId,
                source_amount: sourceAmount,
                source_amount_usd: sourceAmountUSD,
                error_message: errorMessage,
                error_code: errorCode || '',
                transaction_hash: txHash || '',
                wallet_address: walletAddress || '',
                route_plan_length: jupiterRouteDetails?.routePlanLength || 0,
                amm_names: jupiterRouteDetails?.ammNames || [],
                price_impact_pct: jupiterRouteDetails?.priceImpactPct || '0',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked Jupiter swap failed:', {
                sourceToken,
                destinationToken,
                chainId,
                sourceAmount,
                sourceAmountUSD,
                errorMessage,
                errorCode,
                txHash,
                walletAddress,
                jupiterRouteDetails,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking Jupiter swap failure:', error)
        }
    }

    /**
     * Track Jupiter performance metrics
     * @param routeCount Number of routes found
     * @param bestRouteAmmCount Number of AMMs in the best route
     * @param quoteLatencyMs Time taken to get the quote in milliseconds
     * @param priceImpactBps Price impact in basis points
     * @param sourceToken Source token symbol
     * @param destinationToken Destination token symbol
     * @param walletAddress User's wallet address
     */
    public trackJupiterPerformance(
        routeCount: number,
        bestRouteAmmCount: number,
        quoteLatencyMs: number,
        priceImpactBps: number,
        sourceToken: string,
        destinationToken: string,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('jupiter_performance', {
                route_count: routeCount,
                best_route_amm_count: bestRouteAmmCount,
                quote_latency_ms: quoteLatencyMs,
                price_impact_bps: priceImpactBps,
                source_token: sourceToken,
                destination_token: destinationToken,
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked Jupiter performance:', {
                routeCount,
                bestRouteAmmCount,
                quoteLatencyMs,
                priceImpactBps,
                sourceToken,
                destinationToken,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking Jupiter performance:', error)
        }
    }

    /**
     * Track when a user clicks on a swap button
     * @param buttonType The type of swap button clicked (initial, confirmation, wrap, unwrap)
     * @param properties Additional properties to track with the event
     */
    public trackSwapButtonClick(
        buttonType: 'initial' | 'confirmation' | 'wrap' | 'unwrap',
        properties: Record<string, any> = {}
    ): void {
        try {
            this.trackEvent('swap_button_click', {
                button_type: buttonType,
                ...properties,
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked swap button click:', {
                buttonType,
                ...properties,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking swap button click:', error)
        }
    }

    // End of the analytics service methods

    /**
     * Track when a user attempts to wrap a native token
     * @param tokenSymbol The native token symbol (e.g. 'ETH')
     * @param wrappedTokenSymbol The wrapped token symbol (e.g. 'WETH')
     * @param chainId The chain ID of the token
     * @param amount The amount being wrapped (in base units)
     * @param amountUsd The USD value of the amount being wrapped
     * @param walletAddress The user's wallet address
     */
    public trackWrapAttempt(
        tokenSymbol: string,
        wrappedTokenSymbol: string,
        chainId: number,
        amount: string | bigint,
        amountUsd?: number,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('wrap_attempt', {
                token_symbol: tokenSymbol,
                wrapped_token_symbol: wrappedTokenSymbol,
                chain_id: chainId,
                chain_name: getChainNameFromChainId(chainId) || String(chainId),
                amount: String(amount),
                amount_usd: amountUsd || 0,
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked wrap attempt:', {
                tokenSymbol,
                wrappedTokenSymbol,
                chainId,
                amount: String(amount),
                amountUsd,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking wrap attempt:', error)
        }
    }

    /**
     * Track when a wrap transaction is started (submitted to the blockchain)
     * @param tokenSymbol The native token symbol (e.g. 'ETH')
     * @param wrappedTokenSymbol The wrapped token symbol (e.g. 'WETH')
     * @param chainId The chain ID of the token
     * @param amount The amount being wrapped (in base units)
     * @param txHash The transaction hash
     * @param walletAddress The user's wallet address
     */
    public trackWrapStarted(
        tokenSymbol: string,
        wrappedTokenSymbol: string,
        chainId: number,
        amount: string | bigint,
        txHash: string,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('wrap_started', {
                token_symbol: tokenSymbol,
                wrapped_token_symbol: wrappedTokenSymbol,
                chain_id: chainId,
                chain_name: getChainNameFromChainId(chainId) || String(chainId),
                amount: String(amount),
                transaction_hash: txHash,
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked wrap started:', {
                tokenSymbol,
                wrappedTokenSymbol,
                chainId,
                amount: String(amount),
                txHash,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking wrap started:', error)
        }
    }

    /**
     * Track when a wrap transaction completes successfully
     * @param tokenSymbol The native token symbol (e.g. 'ETH')
     * @param wrappedTokenSymbol The wrapped token symbol (e.g. 'WETH')
     * @param chainId The chain ID of the token
     * @param amount The amount wrapped (in base units)
     * @param txHash The transaction hash
     * @param gasUsed The gas used for the transaction (optional)
     * @param gasPrice The gas price used (optional)
     * @param walletAddress The user's wallet address
     */
    public trackWrapSuccess(
        tokenSymbol: string,
        wrappedTokenSymbol: string,
        chainId: number,
        amount: string | bigint,
        txHash: string,
        gasUsed?: string | number,
        gasPrice?: string | number,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('wrap_success', {
                token_symbol: tokenSymbol,
                wrapped_token_symbol: wrappedTokenSymbol,
                chain_id: chainId,
                chain_name: getChainNameFromChainId(chainId) || String(chainId),
                amount: String(amount),
                transaction_hash: txHash,
                gas_used: gasUsed ? String(gasUsed) : '',
                gas_price: gasPrice ? String(gasPrice) : '',
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked wrap success:', {
                tokenSymbol,
                wrappedTokenSymbol,
                chainId,
                amount: String(amount),
                txHash,
                gasUsed,
                gasPrice,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking wrap success:', error)
        }
    }

    /**
     * Track when a wrap transaction fails
     * @param tokenSymbol The native token symbol (e.g. 'ETH')
     * @param wrappedTokenSymbol The wrapped token symbol (e.g. 'WETH')
     * @param chainId The chain ID of the token
     * @param amount The amount that was being wrapped (in base units)
     * @param errorMessage The error message
     * @param errorCode The error code if available
     * @param txHash The transaction hash if available
     * @param walletAddress The user's wallet address
     */
    public trackWrapFailed(
        tokenSymbol: string,
        wrappedTokenSymbol: string,
        chainId: number,
        amount: string | bigint,
        errorMessage: string,
        errorCode?: string | number,
        txHash?: string,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('wrap_failed', {
                token_symbol: tokenSymbol,
                wrapped_token_symbol: wrappedTokenSymbol,
                chain_id: chainId,
                chain_name: getChainNameFromChainId(chainId) || String(chainId),
                amount: String(amount),
                error_message: errorMessage,
                error_code: errorCode ? String(errorCode) : '',
                transaction_hash: txHash || '',
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked wrap failed:', {
                tokenSymbol,
                wrappedTokenSymbol,
                chainId,
                amount: String(amount),
                errorMessage,
                errorCode,
                txHash,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking wrap failed:', error)
        }
    }

    /**
     * Track when a user attempts to unwrap a token
     * @param wrappedTokenSymbol The wrapped token symbol (e.g. 'WETH')
     * @param tokenSymbol The native token symbol (e.g. 'ETH')
     * @param chainId The chain ID of the token
     * @param amount The amount being unwrapped (in base units)
     * @param amountUsd The USD value of the amount being unwrapped
     * @param walletAddress The user's wallet address
     */
    public trackUnwrapAttempt(
        wrappedTokenSymbol: string,
        tokenSymbol: string,
        chainId: number,
        amount: string | bigint,
        amountUsd?: number,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('unwrap_attempt', {
                wrapped_token_symbol: wrappedTokenSymbol,
                token_symbol: tokenSymbol,
                chain_id: chainId,
                chain_name: getChainNameFromChainId(chainId) || String(chainId),
                amount: String(amount),
                amount_usd: amountUsd || 0,
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked unwrap attempt:', {
                wrappedTokenSymbol,
                tokenSymbol,
                chainId,
                amount: String(amount),
                amountUsd,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking unwrap attempt:', error)
        }
    }

    /**
     * Track when an unwrap transaction is started (submitted to the blockchain)
     * @param wrappedTokenSymbol The wrapped token symbol (e.g. 'WETH')
     * @param tokenSymbol The native token symbol (e.g. 'ETH')
     * @param chainId The chain ID of the token
     * @param amount The amount being unwrapped (in base units)
     * @param txHash The transaction hash
     * @param walletAddress The user's wallet address
     */
    public trackUnwrapStarted(
        wrappedTokenSymbol: string,
        tokenSymbol: string,
        chainId: number,
        amount: string | bigint,
        txHash: string,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('unwrap_started', {
                wrapped_token_symbol: wrappedTokenSymbol,
                token_symbol: tokenSymbol,
                chain_id: chainId,
                chain_name: getChainNameFromChainId(chainId) || String(chainId),
                amount: String(amount),
                transaction_hash: txHash,
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked unwrap started:', {
                wrappedTokenSymbol,
                tokenSymbol,
                chainId,
                amount: String(amount),
                txHash,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking unwrap started:', error)
        }
    }

    /**
     * Track when an unwrap transaction completes successfully
     * @param wrappedTokenSymbol The wrapped token symbol (e.g. 'WETH')
     * @param tokenSymbol The native token symbol (e.g. 'ETH')
     * @param chainId The chain ID of the token
     * @param amount The amount unwrapped (in base units)
     * @param txHash The transaction hash
     * @param gasUsed The gas used for the transaction (optional)
     * @param gasPrice The gas price used (optional)
     * @param walletAddress The user's wallet address
     */
    public trackUnwrapSuccess(
        wrappedTokenSymbol: string,
        tokenSymbol: string,
        chainId: number,
        amount: string | bigint,
        txHash: string,
        gasUsed?: string | number,
        gasPrice?: string | number,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('unwrap_success', {
                wrapped_token_symbol: wrappedTokenSymbol,
                token_symbol: tokenSymbol,
                chain_id: chainId,
                chain_name: getChainNameFromChainId(chainId) || String(chainId),
                amount: String(amount),
                transaction_hash: txHash,
                gas_used: gasUsed ? String(gasUsed) : '',
                gas_price: gasPrice ? String(gasPrice) : '',
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked unwrap success:', {
                wrappedTokenSymbol,
                tokenSymbol,
                chainId,
                amount: String(amount),
                txHash,
                gasUsed,
                gasPrice,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking unwrap success:', error)
        }
    }

    /**
     * Track when an unwrap transaction fails
     * @param wrappedTokenSymbol The wrapped token symbol (e.g. 'WETH')
     * @param tokenSymbol The native token symbol (e.g. 'ETH')
     * @param chainId The chain ID of the token
     * @param amount The amount that was being unwrapped (in base units)
     * @param errorMessage The error message
     * @param errorCode The error code if available
     * @param txHash The transaction hash if available
     * @param walletAddress The user's wallet address
     */
    public trackUnwrapFailed(
        wrappedTokenSymbol: string,
        tokenSymbol: string,
        chainId: number,
        amount: string | bigint,
        errorMessage: string,
        errorCode?: string | number,
        txHash?: string,
        walletAddress?: string
    ): void {
        try {
            this.trackEvent('unwrap_failed', {
                wrapped_token_symbol: wrappedTokenSymbol,
                token_symbol: tokenSymbol,
                chain_id: chainId,
                chain_name: getChainNameFromChainId(chainId) || String(chainId),
                amount: String(amount),
                error_message: errorMessage,
                error_code: errorCode ? String(errorCode) : '',
                transaction_hash: txHash || '',
                wallet_address: walletAddress || '',
                timestamp: new Date().toISOString(),
            })

            logger.debug('[AnalyticsService] Tracked unwrap failed:', {
                wrappedTokenSymbol,
                tokenSymbol,
                chainId,
                amount: String(amount),
                errorMessage,
                errorCode,
                txHash,
                walletAddress,
            })
        } catch (error) {
            logger.error('[AnalyticsService] Error tracking unwrap failed:', error)
        }
    }
}

// Export a singleton instance
const analyticsService = new AnalyticsService()

// Export both as default and named export to support various import styles
export { analyticsService }
export default analyticsService
